import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Modal from 'components/Modal';

import { nullFunction } from 'utils';

import StyledContainer from './SimilarPagesModal.styled';

const SimilarPagesModal = ({
  similarPages,
  resetSimilarPages,
  addSimilarPages,
  limit,
  pages: existingPages,
}) => {
  const [pages, setPages] = useState([]);
  const [showLimitError, setShowLimitError] = useState(false);

  const toggleSelect = useCallback(
    ({ href, isScreenshot }) =>
      (e) => {
        if (e) e.stopPropagation();

        setShowLimitError(false);

        setPages((state) => {
          if (
            !state.some((s) => s.href === href) &&
            limit !== -1 &&
            existingPages.length + state.length >= limit
          ) {
            setShowLimitError(true);
            return state;
          }

          return state.some((s) => s.href === href)
            ? state.filter((s) => s.href !== href)
            : [...state, { href, isScreenshot }];
        });
      },
    [limit, existingPages],
  );

  const isPageAdded = useCallback(
    (href) => pages.some((s) => s.href === href),
    [pages],
  );

  const isAllPagesAdded = useCallback(
    () => similarPages.pages.length === pages.length,
    [similarPages, pages],
  );

  const onAddSimilarPages = useCallback(() => {
    addSimilarPages(pages);
    setPages([]);
  }, [addSimilarPages, pages]);

  const toggleSelectAll = useCallback(() => {
    (isAllPagesAdded()
      ? similarPages.pages
      : similarPages.pages.filter(({ href }) => !isPageAdded(href))
    ).forEach(({ href, isScreenshot }) => {
      toggleSelect({ href, isScreenshot })();
    });
  }, [similarPages, isAllPagesAdded, isPageAdded, toggleSelect]);

  return (
    <Modal
      show={similarPages.show}
      toggleModal={resetSimilarPages}
      contentProps={{ style: { padding: '60px 60px 70px' } }}>
      <StyledContainer>
        <img src="/assets/img/pages.png" alt="domino" />
        {similarPages.url && (
          <>
            <h3>
              We found the following pages on{' '}
              {new URL(similarPages.url)?.hostname}. Add the pages you want to
              have in your project
            </h3>
            <ul>
              {similarPages.pages.length ? (
                <li>
                  <Button
                    type="button"
                    className={isAllPagesAdded() ? 'is-active' : ''}
                    onClick={toggleSelectAll}>
                    <Icon name="check" verticalAlign="middle" />
                    <span style={{ fontWeight: 600 }}>Select all</span>
                  </Button>
                </li>
              ) : null}
              {similarPages.pages.map(({ text, href, isScreenshot }) => (
                <li key={href}>
                  <Button
                    type="button"
                    className={isPageAdded(href) ? 'is-active' : ''}
                    onClick={toggleSelect({ href, isScreenshot })}>
                    <Icon name="check" verticalAlign="middle" />
                    <span>{text}</span>
                  </Button>
                </li>
              ))}
            </ul>
            {showLimitError && (
              <p className="red">
                {`You can add a maximum of ${limit} pages in this project. `}
                <Link
                  to="/upgrade"
                  className="link"
                  onClick={resetSimilarPages}>
                  Upgrade now
                </Link>
              </p>
            )}
            <Button
              type="button"
              styled
              disabled={pages.length === 0}
              onClick={pages.length === 0 ? nullFunction : onAddSimilarPages}>
              Add Pages
            </Button>
            <Button type="button" className="link" onClick={resetSimilarPages}>
              Skip
            </Button>
          </>
        )}
      </StyledContainer>
    </Modal>
  );
};

SimilarPagesModal.propTypes = {
  similarPages: PropTypes.object.isRequired,
  resetSimilarPages: PropTypes.func.isRequired,
  addSimilarPages: PropTypes.func.isRequired,
  limit: PropTypes.number.isRequired,
  pages: PropTypes.array.isRequired,
};

export default SimilarPagesModal;
